import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../states/user/user-selector';
import { StudentDirectApplicationService } from '../../../services/student-direct-application/student-direct-application.service';
import { filter, take, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MentorService } from '../../../services/account/mentor.service';
import { MentorType } from '../../../models/enums/enums';
import { YoutubeService } from '../../../services/youtube/youtube.service';
import { YoutubePlaylistItemsResponse } from '../../../models/youtube/youtubePlaylistItemsResponse';

@Component({
  selector: 'app-dashboard-promo-sections',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard-promo-sections.component.html',
  styleUrl: './dashboard-promo-sections.component.scss'
})
export class DashboardPromoSectionsComponent implements OnInit, AfterViewInit, OnDestroy {
  
  directApplyCount: number = 0;
  isLoadingDirectApply: boolean = true;
  hasMentor: boolean = false;
  isLoadingMentor: boolean = true;

  videos: YoutubePlaylistItemsResponse[] = [];
  isLoadingVideos: boolean = true;
  isLoadingMoreVideos: boolean = false;
  videoNextToken?: string;

  private videoObserver?: IntersectionObserver;
  private anchorElement?: HTMLElement;

  @ViewChild('carouselContainer') carouselContainer?: ElementRef;

  @ViewChild('videoScrollAnchor') set videoScrollAnchor(el: ElementRef) {
    if (el && el.nativeElement !== this.anchorElement) {
      this.anchorElement = el.nativeElement;
      setTimeout(() => this.setupVideoScroll(), 100);
    }
  }

  constructor(
    private router: Router,
    private store: Store,
    private studentDirectApplicationService: StudentDirectApplicationService,
    private mentorService: MentorService,
    private youtubeService: YoutubeService
  ) {}

  ngOnInit() {
    const raw = localStorage.getItem('account');
    if (raw) {
      try {
        const account = JSON.parse(raw);
        this.hasMentor = !!account?.hasAssignedMentor;
      } catch (e) {
        console.warn('Error parsing account from localStorage', e);
      }
    }
    this.isLoadingMentor = false;

    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user?.id),
      take(1)
    ).subscribe(user => {
      this.studentDirectApplicationService
        .getByStudentId({ studentId: user.id })
        .subscribe({
          next: (response: any) => {
            let rawItems: any[] = [];
            if (Array.isArray(response)) {
              rawItems = response;
            } else if (response?.result) {
              rawItems = Array.isArray(response.result) ? response.result : [response.result];
            } else if (response && response.instituteId) {
              rawItems = [response];
            }
            this.directApplyCount = rawItems.length;
            this.isLoadingDirectApply = false;
          },
          error: () => {
            this.isLoadingDirectApply = false;
          }
        });
    });

    this.fetchVideos(false);
  }

  ngAfterViewInit() {
    this.setupVideoScroll();
  }

  ngOnDestroy() {
    this.videoObserver?.disconnect();
  }

  fetchVideos(isMore: boolean = false) {
    if (isMore) this.isLoadingMoreVideos = true;
    else this.isLoadingVideos = true;

    const token = isMore ? this.videoNextToken : undefined;

    this.youtubeService.getVideos(token).pipe(
      take(1),
      catchError(() => {
        this.isLoadingVideos = false;
        this.isLoadingMoreVideos = false;
        return of({ items: [], nextPageToken: undefined });
      })
    ).subscribe((response: any) => {
      if (isMore) {
        this.videos = [...this.videos, ...(response.items || [])];
      } else {
        this.videos = response.items || [];
      }
      this.videoNextToken = response.nextPageToken;
      this.isLoadingVideos = false;
      this.isLoadingMoreVideos = false;
    });
  }

  private setupVideoScroll() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }

    const rootElement = this.carouselContainer?.nativeElement || null;

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.videoNextToken && !this.isLoadingMoreVideos) {
          this.fetchVideos(true);
        }
      });
    }, { root: rootElement, threshold: 0.1 });

    if (this.anchorElement) {
      this.videoObserver.observe(this.anchorElement);
    }
  }

  goToScholarships() {
    this.router.navigate(['/portal/institute-search'], { queryParams: { tab: 'scholarships' } });
  }

  openDirectApply() {
    this.router.navigate(['portal', 'da-forms']);
  }

  openMentorConnect() {
    this.router.navigate(['portal', 'mentor-me']);
  }
  
  openVideoUrl(videoId: string | undefined) {
    if (videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
    }
  }
}